import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Toaster } from "react-hot-toast";
import ColumnSelection from "./ColumnSelection";
import ConfigPopup from "./ConfigPopup";
import heartLoading from "../../../assests/heartLoading.gif";
import useSmartToast, {ErrorDetailModal} from "./components/useSmartToast";

import {
  AssetSelectionSidebar,
  SelectedItemsPanel,
} from "./components/AssetSelectionContent";
import {
  filterTree,
  getAllSelectableItems,
} from "./components/AssetSelectionHelpers";

const TABS = [
  { key: "adls", label: "ADLS" },
  { key: "uc", label: "Unity Catalog" },
  { key: "manual", label: "Manual Upload" },
  { key: "uc_gcp", label: "UC GCP" },
];

const isSelectableItem = (item, source) => {
  if (source === "uc" || source === "uc_gcp") {
    return !item.children || item.type === "table";
  }
  if (source === "adls") {
    return true;
  }
  return false;
};

const AssetSelectionModal = ({
  id,
  selectedSources = [],
  nextStep,
  previousStep,
  region,
  country,
  columnScreen,
}) => {
  const availableTabs = TABS.filter((tab) => selectedSources.includes(tab.key));
  const [activeTab, setActiveTab] = useState(availableTabs[0]?.key || "adls");

  const [data, setData] = useState({ ADLS: [], UC: [], UCGCP: [] });
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  const [editingNames, setEditingNames] = useState({});
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [configuringFileId, setConfiguringFileId] = useState(null);
  const [isFile, setIsFile] = useState(false);
  const [configuringFileName, setConfiguringFileName] = useState(null);
  const [configuringFileSource, setConfiguringFileSource] = useState(null);
  const [fileConfigurations, setFileConfigurations] = useState({});
  const [isEditingExistingConfig, setIsEditingExistingConfig] = useState(false);

  const [configForm, setConfigForm] = useState({
    assetName: "",
    assetDescription: "",
    dataAssetOwner: "",
    dataframeName: "",
    region: "",
    market: "",
    technicalDataSteward: "",
    completeness: "",
    accuracy: "",
    consistency: "",
    freshness: "",
    uniqueness: "",
    validity: "",
    sampleSize: 10,
    tags: [],
    frequency: "",
    startDate: "",
    endDate: "",
    time: "",
    daysOfWeek: [],
    dayOfMonth: "",
    selectedPeriods: [],
    workSpaceDevUrl: "",
    workSpaceProdUrl: "",
    dataAssetJobType: "in_motion", 
  });

  const [formErrors, setFormErrors] = useState({});
  const [columnModalOpen, setColumnModalOpen] = useState(
    columnScreen ? true : false
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [onboardindDetails, setOnboardingDetails] = useState({});
  const [regionsCountriesData, setRegionsCountriesData] = useState([]);
  const [columnSubmitting, setColumnSubmitting] = useState(false);

  const { expandedToast, closeExpandedToast, showSmartToast, extractErrorMessage } = useSmartToast();

  useEffect(() => {
    const fetchEditData = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/edit-form/${id}`
        );
        if (response.status === 200 && response.data) {
          const data = response.data;
          setOnboardingDetails(data);
        }
      } catch (error) {
        toast.error("Failed to fetch product for editing");
      } finally {
        setLoading(false);
      }
    };
    fetchEditData();
  }, [id]);

  const adlsData = useMemo(() => data.ADLS || [], [data.ADLS]);
  const ucData = useMemo(() => data.UC || [], [data.UC]);
  const ucGcpData = useMemo(() => data.UCGCP || [], [data.UCGCP]);
  const [columnsList, setColumnList] = useState([]);
  const [assetNameCheckMessage, setAssetNameCheckMessage] = useState("");
  const [assetNameIsUnique, setAssetNameIsUnique] = useState(undefined);
  const [disableAllFields, setDisableAllFields] = useState(false);
  const [allFieldsValid, setAllFieldsValid] = useState(true);
  const [formTouched, setFormTouched] = useState(false);
  const [ErrorMessage, SetErrorMessage] = useState({});
  let debounceTimeout = null;

  useEffect(() => {
    let isMounted = true;
    const fetchAllData = async () => {
      setLoadingAssets(true);
      try {
        const [browsingRes, selectionRes, regionsCountriesRes] =
          await Promise.all([
            axios.get(`${process.env.REACT_APP_API_URL}/assets/list/${id}`),
            axios.get(`${process.env.REACT_APP_API_URL}/get-data-asset-details/${id}`),
            axios.get(`${process.env.REACT_APP_API_URL}/regions-countries-list/`),
          ]);
          

        let processedData = { ADLS: [], UC: [], UCGCP: [] };
        SetErrorMessage(browsingRes.data?.message || {});
        if (browsingRes.data) {
          if (browsingRes.data.adls || browsingRes.data.ADLS) {
            processedData.ADLS = browsingRes.data.adls || browsingRes.data.ADLS || [];
          }
          if (browsingRes.data.uc || browsingRes.data.UC) {
            processedData.UC = browsingRes.data.uc || browsingRes.data.UC || [];
          }
          if (browsingRes.data.uc_gcp || browsingRes.data.UC_GCP) {
            processedData.UCGCP = browsingRes.data.uc_gcp || browsingRes.data.UC_GCP || [];
          }
          if (Array.isArray(browsingRes.data)) {
            processedData.ADLS = browsingRes.data;
          }
        }
        if (isMounted) setData(processedData);

        if (regionsCountriesRes.data && isMounted) {
          setRegionsCountriesData(regionsCountriesRes.data);
        }

        if (selectionRes.data) {
          const { selectedFolders = [], uploadedFiles = [] } = selectionRes.data;
          if (isMounted) {
            const normalizedSelectedFolders = selectedFolders.map((item) => ({
              ...item,
              path: item.path || item.full_path || item.name,
              source: item.source || "adls",
              type: item.type || "table",
              full_path: item.full_path || item.path,
            }));

            setSelectedFolders(normalizedSelectedFolders);
            setUploadedFiles(uploadedFiles);

            const newConfigs = {};
            normalizedSelectedFolders.forEach((f) => {
              if (f.configuration) newConfigs[f.path] = f.configuration;
            });
            uploadedFiles.forEach((f) => {
              if (f.configuration) newConfigs[f.id || f.name] = f.configuration;
            });
            setFileConfigurations(newConfigs);
          }
        } else {
          if (isMounted) {
            setSelectedFolders([]);
            setUploadedFiles([]);
            setFileConfigurations({});
          }
        }
      } catch (error) {
        toast.error("Failed to fetch asset or selection data.");
      } finally {
        if (isMounted) setLoadingAssets(false);
      }
    };
    fetchAllData();
    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (availableTabs.length > 0) {
      setActiveTab(availableTabs[0].key);
    }
  }, [selectedSources]);

  const processFiles = (files) => {
    const newFiles = Array.from(files).map((file) => ({
      file,
      id: `${file.name}-${file.lastModified}-${Math.random()}`,
      name: file.name,
      size: file.size,
      lastModified: file.lastModified,
      type: file.type,
    }));
    setUploadedFiles((prev) => [...prev, ...newFiles]);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const csvFiles = Array.from(e.dataTransfer.files).filter((file) =>
        file.name.endsWith(".csv")
      );
      if (csvFiles.length > 0) {
        processFiles(csvFiles);
      } else {
        toast.error("Only CSV files are accepted.");
      }
      e.dataTransfer.clearData();
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
  };

  const normalizePath = (path) => {
    if (!path) return "";
    return path.replace(/\./g, "/").replace(/\\/g, "/").toLowerCase().trim();
  };

  const handleItemSelect = (item, shouldSelect) => {
    if (!isSelectableItem(item, item.source)) {
      return;
    }
    const targetPath = normalizePath(item.path || item.full_path);
    setSelectedFolders((prev) => {
      if (shouldSelect) {
        if (prev.some((selected) => selected.source === item.source && normalizePath(selected.path || selected.full_path) === targetPath)) {
          return prev;
        }
        return [...prev, { ...item }];
      }
      return prev.filter((selected) => !(selected.source === item.source && normalizePath(selected.path || selected.full_path) === targetPath));
    });
  };

  const handleSelectAll = () => {
    const currentData =
      activeTab === "adls" ? adlsData
      : activeTab === "uc" ? ucData
      : activeTab === "uc_gcp" ? ucGcpData
      : [];

    const allItems = getAllSelectableItems(currentData, activeTab, isSelectableItem);

    const removeDuplicateRoot = (path) => {
      const parts = path.split("/");
      while (parts.length > 1 && parts[0] === parts[1]) {
        parts.splice(1, 1);
      }
      return parts.join("/");
    };

    const newSelected = allItems
      .filter((item) => {
        let itemPath = normalizePath(item.path || item.full_path);
        itemPath = removeDuplicateRoot(itemPath);
        return !selectedFolders.some((sel) => {
          let selPath = normalizePath(sel.path || sel.full_path);
          selPath = removeDuplicateRoot(selPath);
          return sel.source === item.source && selPath === itemPath;
        });
      })
      .map((item) => {
        let cleanedPath = normalizePath(item.path || item.full_path);
        cleanedPath = removeDuplicateRoot(cleanedPath);
        return { ...item, path: cleanedPath, full_path: cleanedPath };
      });

    setSelectedFolders((prev) => [...prev, ...newSelected]);
  };

  const handleClear = () => {
    const source = activeTab;
    setSelectedFolders((prev) => prev.filter((item) => item.source !== source));
  };

  const handleRemoveFolder = (path) => {
    setSelectedFolders((prev) => prev.filter((folder) => folder.path !== path));
  };

  const handleRemoveFile = (id) => {
    setUploadedFiles((prev) => prev.filter((fileObj) => fileObj.id !== id));
  };

  const buildConfiguration = (config, fallback = {}) => ({
    assetName: config?.assetName || fallback.assetName,
    assetDescription: config?.assetDescription || fallback.assetDescription,
    dataAssetOwner: config?.dataAssetOwner || fallback.dataAssetOwner,
    dataframeName: config?.dataframeName || fallback.dataframeName,
    region: config?.region || fallback.region,
    market: config?.market || fallback.market,
    technicalDataSteward: config?.technicalDataSteward || fallback.technicalDataSteward,
    completeness: config?.completeness || fallback.completeness,
    uniqueness: config?.uniqueness || fallback.uniqueness,
    validity: config?.validity || fallback.validity,
    accuracy: config?.accuracy || fallback.accuracy,
    consistency: config?.consistency || fallback.consistency,
    freshness: config?.freshness || fallback.freshness || "",
    sampleSize: config?.sampleSize || fallback.sampleSize || 10,
    tags: config?.tags || fallback.tags || [],
    frequency: config?.frequency || fallback.frequency,
    startDate: config?.startDate || fallback.startDate,
    endDate: config?.endDate || fallback.endDate,
    time: config?.time || fallback.time || "",
    daysOfWeek: config?.daysOfWeek || fallback.daysOfWeek || [],
    dayOfMonth: config?.dayOfMonth || fallback.dayOfMonth || "",
    selectedPeriods: config?.selectedPeriods || fallback.selectedPeriods || [],
    workSpaceDevUrl: config?.workSpaceDevUrl || fallback.workSpaceDevUrl || "",
    workSpaceProdUrl: config?.workSpaceProdUrl || fallback.workSpaceProdUrl || "",
    isAtRest: (config?.dataAssetJobType || fallback.dataAssetJobType || "in_motion") === "at_rest",
  });

  const buildPayload = () => ({
    selectedFolders: selectedFolders.map((folder) => ({
      ...folder,
      path: folder.path || folder.full_path || folder.name,
      configuration: buildConfiguration(fileConfigurations[folder.path], folder),
    })),
    uploadedFiles: uploadedFiles.map((file) => ({
      ...file,
      configuration: buildConfiguration(fileConfigurations[file.id], file),
    })),
    timestamp: new Date().toISOString(),
    totalItems: selectedFolders.length + uploadedFiles.length,
    product_id: id,
    created_by: localStorage.getItem("userId"),
  });

  const handleSave = async () => {
    setIsSaving(true);
    const formData = new FormData();
    uploadedFiles.forEach((fileObj) => {
      formData.append("files", fileObj.file);
    });
    formData.append("product_id", id);
    formData.append("payload", JSON.stringify(buildPayload()));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/save/data-asset-onboarding`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.status === 200 || response.status === 201) {
        toast.success("Assets saved successfully!");
      } else {
        showSmartToast("Failed to save assets.");
      }
    } catch (error) {
      showSmartToast(extractErrorMessage(error, "Failed to save assets. Please try again later."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const formData = new FormData();
    uploadedFiles.forEach((fileObj) => {
      formData.append("files", fileObj.file);
    });
    formData.append("product_id", id);
    formData.append("payload", JSON.stringify(buildPayload()));

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/data-asset-onboarding/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.status === 200 || response.status === 201) {
        setColumnList(response?.data?.schemas);
        toast.success("Assets submitted successfully!");
        setTimeout(() => setColumnModalOpen(true), 1000);
      }
    } catch (error) {
      showSmartToast(extractErrorMessage(error, "Failed to Submit assets. Please try again later."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditingNameChange = (id, value) => {
    setEditingNames((prev) => ({ ...prev, [id]: value }));
  };

  const handleNameSave = (id, newName, isFolder) => {
    if (isFolder) {
      setSelectedFolders((prev) =>
        prev.map((folder) =>
          folder.path === id ? { ...folder, assetName: newName, isEditing: false } : folder
        )
      );
    } else {
      setUploadedFiles((prev) =>
        prev.map((fileObj) =>
          fileObj.id === id ? { ...fileObj, assetName: newName, isEditing: false } : fileObj
        )
      );
    }
    setEditingNames((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };

  const totalItems = selectedFolders.length + uploadedFiles.length;

  const openConfigModal = (isFile, itemSource, itemKey, itemName) => {
    setConfiguringFileId(itemKey);
    setConfiguringFileName(itemName);
    setConfiguringFileSource(itemSource);
    setIsFile(isFile);
    setConfigModalOpen(true);
  };

  const closeConfigModal = () => {
    setConfiguringFileId(null);
    setConfiguringFileName(null);
    setConfigModalOpen(false);
    setAssetNameCheckMessage("");
    setAssetNameIsUnique(undefined);
    setFormTouched(false);
  };

  useEffect(() => {
    if (configModalOpen && configuringFileId) {
      const existing = fileConfigurations[configuringFileId] || {};
      const isAlreadyConfigured = Object.keys(existing).length > 0;
      setIsEditingExistingConfig(isAlreadyConfigured);

      let assetNameBySource = "";
      if (configuringFileSource === "uc") {
        const filteredData = filterTree(ucData, configuringFileName);
        assetNameBySource = filteredData[0]?.children[0]?.name + "|" + configuringFileName;
      } else if (configuringFileSource === "uc_gcp") {
        const filteredData = filterTree(ucGcpData, configuringFileName);
        assetNameBySource = filteredData[0]?.children[0]?.name + "|" + configuringFileName;
      } else if (configuringFileSource === "adls") {
        assetNameBySource = configuringFileId;
      }

      const selectedAssetName = isFile ? "" : assetNameBySource;
      const assetName = existing.assetName || selectedAssetName;

      const derivedJobType = (existing.workSpaceDevUrl && existing.workSpaceProdUrl)
        ? "at_rest"
        : existing.dataAssetJobType || "in_motion";

      setConfigForm({
        assetName: assetName,
        assetDescription: existing.assetDescription || "",
        dataAssetOwner: existing.dataAssetOwner || localStorage.getItem("userId"),
        dataframeName: existing.dataframeName || "",
        tags: existing.tags || [],
        region:
          existing?.region ||
          (Array.isArray(onboardindDetails?.marketRegion)
            ? onboardindDetails?.marketRegion.join(", ")
            : onboardindDetails?.marketRegion),
        market:
          existing?.market ||
          (Array.isArray(onboardindDetails?.marketCountry)
            ? onboardindDetails?.marketCountry.join(", ")
            : onboardindDetails?.marketCountry),
        technicalDataSteward: existing.technicalDataSteward || "",
        completeness: existing.completeness || "",
        uniqueness: existing.uniqueness || "",
        validity: existing.validity || "",
        accuracy: existing.accuracy || "",
        consistency: existing.consistency || "",
        freshness: existing.freshness || "",
        sampleSize: existing.sampleSize || 10,
        frequency: existing.frequency || "",
        startDate: existing.startDate || "",
        endDate: existing.endDate || "",
        time: existing.time || "",
        daysOfWeek: existing.daysOfWeek || [],
        dayOfMonth: existing.dayOfMonth || "",
        selectedPeriods: existing.selectedPeriods || [],
        workSpaceDevUrl: existing.workSpaceDevUrl || "",
        workSpaceProdUrl: existing.workSpaceProdUrl || "",
        dataAssetJobType: derivedJobType,
      });
      setFormErrors({});

      if (assetName && !isAlreadyConfigured) {
        checkAssetNameUnique(assetName);
      } else if (isAlreadyConfigured) {
        setAssetNameIsUnique(true);
        setDisableAllFields(false);
      }
    }
  }, [configModalOpen, configuringFileId]);

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    if (["completeness", "accuracy", "consistency", "sampleSize", "freshness"].includes(name)) {
      const numValue = Number(value);
      if (numValue > 100) return;
    }
    setConfigForm((prev) => ({ ...prev, [name]: value }));

    if (name === "assetName") {
      setAssetNameCheckMessage("");
      setAssetNameIsUnique(undefined);
      if (debounceTimeout) clearTimeout(debounceTimeout);
      debounceTimeout = setTimeout(() => {
        checkAssetNameUnique(value);
      }, 600);
    }
  };

  const checkAssetNameUnique = async (name) => {
    if (!name) {
      setAssetNameCheckMessage("");
      setAssetNameIsUnique(undefined);
      setDisableAllFields(true);
      return;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/data-asset/check?name=${name}`
      );
      if (response.data) {
        setAssetNameCheckMessage(response.data.message);
        setAssetNameIsUnique(!response.data.matched);
        setDisableAllFields(response.data.matched);
      } else {
        setAssetNameCheckMessage("");
        setAssetNameIsUnique(undefined);
        setDisableAllFields(true);
      }
    } catch (error) {
      setAssetNameCheckMessage("Error checking asset name");
      setAssetNameIsUnique(undefined);
      setDisableAllFields(true);
    }
  };

  useEffect(() => {
    if (!configForm.assetName || assetNameIsUnique === false) {
      setDisableAllFields(true);
    } else {
      setDisableAllFields(false);
    }
  }, [configForm.assetName, assetNameIsUnique]);

  const validateConfigForm = () => {
    const errors = {};
    if (!configForm.assetName) errors.assetName = "Required";
    if (!configForm.assetDescription) errors.assetDescription = "Required";
    if (!configForm.dataAssetOwner) {
      errors.dataAssetOwner = "Required";
    } else {
      const ALLOWED_EMAIL_DOMAINS = ["effem.com"];
      const emailPattern = /^[^\s@]+@([^\s@]+)$/;
      const match = configForm.dataAssetOwner.match(emailPattern);
      if (!match || !ALLOWED_EMAIL_DOMAINS.includes(match[1])) {
        errors.dataAssetOwner = `Email must be from: ${ALLOWED_EMAIL_DOMAINS.join(", ")}`;
      }
    }
    if (!configForm.dataframeName) errors.dataframeName = "Required";
    if (!configForm.region) errors.region = "Required";
    if (!configForm.market) errors.market = "Required";
    if (!configForm.technicalDataSteward) {
      errors.technicalDataSteward = "Required";
    } else {
      const ALLOWED_EMAIL_DOMAINS = ["effem.com"];
      const emailPattern = /^[^\s@]+@([^\s@]+)$/;
      const match = configForm.technicalDataSteward.match(emailPattern);
      if (!match || !ALLOWED_EMAIL_DOMAINS.includes(match[1])) {
        errors.technicalDataSteward = `Email must be from: ${ALLOWED_EMAIL_DOMAINS.join(", ")}`;
      }
    }
    if (!configForm.completeness || Number(configForm.completeness) < 0) errors.completeness = "Min value is 0";
    if (!configForm.uniqueness || Number(configForm.uniqueness) < 0) errors.uniqueness = "Min value is 0";
    if (!configForm.validity || Number(configForm.validity) < 0) errors.validity = "Min value is 0";
    if (!configForm.accuracy || Number(configForm.accuracy) < 0) errors.accuracy = "Min value is 0";
    if (!configForm.consistency || Number(configForm.consistency) < 0) errors.consistency = "Min value is 0";
    if (!configForm.freshness || Number(configForm.freshness) < 0) errors.freshness = "Min value is 0";
    if (!configForm.sampleSize || Number(configForm.sampleSize) < 0 || Number(configForm.sampleSize) > 100)
      errors.sampleSize = "Sample size must be between 1 and 100";
    if (!configForm.frequency) errors.frequency = "Required";
    if (configForm.frequency && configForm.frequency !== "Hourly" && !configForm.time)
      errors.time = "Time is required for selected frequency";
    if (configForm.frequency === "Weekly") {
      if (!configForm.daysOfWeek || configForm.daysOfWeek.length === 0)
        errors.daysOfWeek = "At least one day must be selected for Weekly frequency";
    }
    if (configForm.frequency === "Mars Periodic") {
      if (!configForm.dayOfMonth) errors.dayOfMonth = "Day is required for Mars Periodic frequency";
    }
    if (configForm.frequency === "Mars Quarter") {
      if (!configForm.selectedPeriods || configForm.selectedPeriods.length === 0)
        errors.selectedPeriods = "At least one period must be selected for Mars Quarter";
      if (!configForm.dayOfMonth) errors.dayOfMonth = "Day is required for Mars Quarter frequency";
    }
    if (configForm.frequency === "Mars Year") {
      if (!configForm.selectedPeriods || configForm.selectedPeriods.length === 0)
        errors.selectedPeriods = "One period must be selected for Mars Year";
      if (!configForm.dayOfMonth) errors.dayOfMonth = "Day is required for Mars Year frequency";
    }
    if (configForm.frequency === "Mars Half-Yearly") {
      if (!configForm.selectedPeriods || configForm.selectedPeriods.length === 0)
        errors.selectedPeriods = "At least one period must be selected for Mars Half-Yearly";
      if (!configForm.dayOfMonth) errors.dayOfMonth = "Day is required for Mars Half-Yearly frequency";
    }
    if (configForm.frequency === 'Quarterly') {
      if (!configForm.selectedPeriods || configForm.selectedPeriods.length === 0)
        errors.selectedPeriods = 'At least one month must be selected for Quarterly';
      if (!configForm.dayOfMonth)
        errors.dayOfMonth = 'Day is required for Quarterly frequency';
    }
    if (configForm.frequency === 'Monthly') {
      if (!configForm.dayOfMonth)
        errors.dayOfMonth = 'Day is required for Monthly frequency';
    }
    if (configForm.frequency === 'Yearly') {
      if (!configForm.selectedPeriods || configForm.selectedPeriods.length === 0)
        errors.selectedPeriods = 'One month must be selected for Yearly';
      if (!configForm.dayOfMonth)
        errors.dayOfMonth = 'Day is required for Yearly frequency';
    }
    if (configForm.frequency === 'Half-Yearly') {
      if (!configForm.selectedPeriods || configForm.selectedPeriods.length === 0)
        errors.selectedPeriods = 'At least one month must be selected for Half-Yearly';
      if (!configForm.dayOfMonth)
        errors.dayOfMonth = 'Day is required for Half-Yearly frequency';
    }

    if (configForm.dataAssetJobType === "at_rest") {
      const devFilled = configForm.workSpaceDevUrl?.trim() !== "";
      const prodFilled = configForm.workSpaceProdUrl?.trim() !== "";
      if (devFilled && !prodFilled) {
        errors.workSpaceProdUrl = "Workspace URL (Prod) is required when Dev URL is provided";
      }
      if (prodFilled && !devFilled) {
        errors.workSpaceDevUrl = "Workspace URL (Dev) is required when Prod URL is provided";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const areAllFieldsFilled = () => {
    const isDataAssetOwnerValid = () => {
      if (!configForm.dataAssetOwner) return false;
      const ALLOWED_EMAIL_DOMAINS = ["effem.com"];
      const emailPattern = /^[^\s@]+@([^\s@]+)$/;
      const match = configForm.dataAssetOwner.match(emailPattern);
      return match && ALLOWED_EMAIL_DOMAINS.includes(match[1]);
    };

    const isTechnicalStewardValid = () => {
      if (!configForm.technicalDataSteward) return false;
      const ALLOWED_EMAIL_DOMAINS = ["effem.com"];
      const emailPattern = /^[^\s@]+@([^\s@]+)$/;
      const match = configForm.technicalDataSteward.match(emailPattern);
      return match && ALLOWED_EMAIL_DOMAINS.includes(match[1]);
    };

    const isJobSelectionValid = () => {
      if (configForm.dataAssetJobType !== "at_rest") return true;
      const devFilled = configForm.workSpaceDevUrl?.trim() !== "";
      const prodFilled = configForm.workSpaceProdUrl?.trim() !== "";
      return (devFilled && prodFilled) || (!devFilled && !prodFilled);
    };

    const baseFields =
      configForm.assetName &&
      configForm.assetDescription &&
      isDataAssetOwnerValid() &&
      configForm.dataframeName &&
      configForm.region &&
      configForm.market &&
      isTechnicalStewardValid() &&
      configForm.completeness &&
      configForm.uniqueness &&
      configForm.validity &&
      configForm.accuracy &&
      configForm.consistency &&
      configForm.freshness &&
      configForm.sampleSize &&
      configForm.frequency &&
      isJobSelectionValid(); 

    const needsTime = configForm.frequency && configForm.frequency !== "Hourly";
    const timeOk = needsTime ? !!configForm.time : true;

    if (configForm.frequency === "Weekly")
      return baseFields && timeOk && configForm.daysOfWeek && configForm.daysOfWeek.length > 0;
    if (configForm.frequency === 'Monthly') {
      return baseFields && timeOk && !!configForm.dayOfMonth;
    }
    if (configForm.frequency === 'Yearly') {
      return baseFields && timeOk && configForm.selectedPeriods?.length > 0 && !!configForm.dayOfMonth;
    }
    if (configForm.frequency === 'Half-Yearly') {
      return baseFields && timeOk && configForm.selectedPeriods?.length > 0 && !!configForm.dayOfMonth;
    }
    if (configForm.frequency === 'Quarterly') {
      return baseFields && timeOk && configForm.selectedPeriods?.length > 0 && !!configForm.dayOfMonth;
    }
    if (configForm.frequency === "Mars Periodic")
      return baseFields && timeOk && configForm.dayOfMonth;
    if (configForm.frequency === "Mars Quarter")
      return baseFields && timeOk && configForm.selectedPeriods && configForm.selectedPeriods.length > 0 && configForm.dayOfMonth;
    if (configForm.frequency === "Mars Yearly")
      return baseFields && timeOk && configForm.selectedPeriods && configForm.selectedPeriods.length > 0 && configForm.dayOfMonth;
    if (configForm.frequency === "Mars Half-Yearly")
      return baseFields && timeOk && configForm.selectedPeriods && configForm.selectedPeriods.length > 0 && configForm.dayOfMonth;
    if (configForm.frequency === "Daily") return baseFields && timeOk;
    if (configForm.frequency === "Hourly") return baseFields;

    return baseFields && timeOk;
  };

  useEffect(() => {
    if (!disableAllFields) {
      setAllFieldsValid(areAllFieldsFilled());
    } else {
      setAllFieldsValid(false);
    }
  }, [configForm, disableAllFields]);

  const handleConfigSave = () => {
    setFormTouched(true);
    if (!validateConfigForm()) return;
    setFileConfigurations((prev) => ({
      ...prev,
      [configuringFileId]: { ...configForm },
    }));
    closeConfigModal();
  };

  const allFoldersConfigured = selectedFolders.every((item) => fileConfigurations[item.path]);
  const allFilesConfigured = uploadedFiles.every((file) => fileConfigurations[file.id]);
  const allItemsConfigured = allFoldersConfigured && allFilesConfigured;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50 w-full h-full">
        <div className="flex flex-col items-center space-y-3">
          <img src={heartLoading} alt="Loading..." className="h-30" />
          <span className="text-gray-700 text-[20px]">
            Hang tight! The resources are loading...
          </span>
        </div>
      </div>
    );
  }

  return (
    <>
      <ErrorDetailModal message={expandedToast} onClose={closeExpandedToast} />

      {!columnModalOpen && (
        <>
          <div className="flex flex-col w-full h-[100%] min-h-[600px] p-6 bg-white">
            <div className="p-6 pt-0 border-b">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <h2 className="text-xl font-bold text-[#FF03F3]">
                    Data Asset Selection
                  </h2>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-sm font-medium bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                    {totalItems} total items
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-grow flex overflow-hidden">
              <AssetSelectionSidebar
                activeTab={activeTab}
                availableTabs={availableTabs}
                setActiveTab={setActiveTab}
                selectedFolders={selectedFolders}
                uploadedFiles={uploadedFiles}
                adlsData={adlsData}
                ucData={ucData}
                ucGcpData={ucGcpData}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                isDragActive={isDragActive}
                handleDrag={handleDrag}
                handleDrop={handleDrop}
                handleFileChange={handleFileChange}
                handleSelectAll={handleSelectAll}
                handleClear={handleClear}
                handleItemSelect={handleItemSelect}
                loading={loading || loadingAssets}
                ErrorMessage={ErrorMessage}
              />
              <SelectedItemsPanel
                selectedFolders={selectedFolders}
                uploadedFiles={uploadedFiles}
                handleNameSave={handleNameSave}
                onEditingNameChange={handleEditingNameChange}
                editingNames={editingNames}
                fileConfigurations={fileConfigurations}
                openConfigModal={openConfigModal}
                handleRemoveFolder={handleRemoveFolder}
                handleRemoveFile={handleRemoveFile}
                allItemsConfigured={allItemsConfigured}
                isSaving={isSaving}
                isSubmitting={isSubmitting}
                handleSave={handleSave}
                handleSubmit={handleSubmit}
                columnModalOpen={columnModalOpen}
              />
            </div>
          </div>
          <Toaster position="top-right" />
          <ConfigPopup
            configForm={configForm}
            configModalOpen={configModalOpen}
            closeConfigModal={closeConfigModal}
            handleConfigChange={handleConfigChange}
            formErrors={formErrors}
            handleConfigSave={handleConfigSave}
            disableAllFields={disableAllFields}
            assetNameCheckMessage={assetNameCheckMessage}
            assetNameIsUnique={assetNameIsUnique}
            allFieldsValid={allFieldsValid}
            formTouched={formTouched}
            regionsCountriesData={regionsCountriesData}
            setConfigForm={setConfigForm}
            isEditingExistingConfig={isEditingExistingConfig}
          />
        </>
      )}

      {columnModalOpen && (
        <ColumnSelection
          nextStep={nextStep}
          columnsList={columnsList}
          id={id}
          setLoading={setLoading}
        />
      )}
    </>
  );
};

export default AssetSelectionModal;